// Course catalog for Synergy Multimedia Academy
// Used for course listings and SEO schema generation

export const courses = [
  // ── Beginner ─────────────────────────────────────────────────────────
  {
    title: 'Professional Photography',
    code: 'PHOTO-101',
    duration: '3 months',
    fee: '450,000 UGX',
    description: 'Master camera basics, lighting, composition + AI-powered upscaling and smart retouching.',
    category: 'Photography',
    level: 'Beginner',
    url: '/courses/professional-photography',
    advantages: [
      'Hands-on practice with professional DSLR and mirrorless cameras',
      'Learn industry-standard lighting techniques and composition rules',
      'Master AI-powered editing tools for stunning image enhancement',
      'Build a professional portfolio during the course',
      'Small class sizes ensure personalized attention'
    ],
    jobOpportunities: [
      'Freelance Portrait & Event Photographer',
      'Studio Photographer',
      'Social Media Content Creator',
      'Product Photography Specialist',
      'Wedding & Events Photographer'
    ],
    whatYouCanDo: [
      'Shoot professional portraits, events, and lifestyle photography',
      'Start your own photography business',
      'Offer photo services to brands and agencies',
      'Create stunning content for social media and marketing',
      'Work with magazines, newspapers, and online publications'
    ]
  },
  {
    title: 'Photo Editing & Post-Production',
    code: 'PHOTO-102',
    duration: '3 months',
    fee: '450,000 UGX',
    description: 'Color correction, retouching with Photoshop + Generative Fill and AI background replacement.',
    category: 'Photography',
    level: 'Beginner',
    url: '/courses/photo-editing-post-production',
    advantages: [
      'Master Adobe Photoshop and Lightroom from scratch',
      'Learn cutting-edge AI tools like Generative Fill and Neural Filters',
      'Develop a keen eye for color grading and image enhancement',
      'Fast-track your editing workflow with professional shortcuts',
      'Create magazine-quality retouching and composites'
    ],
    jobOpportunities: [
      'Photo Retoucher for Studios & Agencies',
      'Freelance Photo Editor',
      'E-commerce Product Image Editor',
      'Magazine & Publication Retoucher',
      'Real Estate Photo Editor'
    ],
    whatYouCanDo: [
      'Transform raw photos into stunning final images',
      'Offer photo editing services to photographers and businesses',
      'Create professional composites and manipulations',
      'Edit product photos for online stores',
      'Retouch portraits for models, influencers, and brands'
    ]
  },
  {
    title: 'Videography (Camera Work)',
    code: 'VIDEO-CAM-001',
    duration: '3 months',
    fee: '550,000 UGX',
    description: 'Professional videography, camera operation + AI-driven auto-tracking and scene detection.',
    category: 'Video Production',
    level: 'Beginner',
    url: '/courses/videography-camera-work',
    advantages: [
      'Hands-on training with professional video cameras and equipment',
      'Learn cinematic camera movements and framing techniques',
      'Master lighting setups for interviews, products, and events',
      'Practice with gimbal stabilizers and modern shooting tools',
      'Understand AI-powered auto-tracking for smooth footage'
    ],
    jobOpportunities: [
      'Videographer for Events & Weddings',
      'Corporate Video Producer',
      'YouTube & Content Creator Videographer',
      'Music Video Cameraman',
      'Documentary Filmmaker'
    ],
    whatYouCanDo: [
      'Shoot professional videos for events, brands, and clients',
      'Create cinematic content for social media and YouTube',
      'Film corporate videos, commercials, and promotional content',
      'Work as a camera operator on film and TV productions',
      'Start your own video production business'
    ]
  },
  {
    title: 'Video Editing (Post-Production)',
    code: 'VIDEO-EDIT-001',
    duration: '3 months',
    fee: '550,000 UGX',
    description: 'Video editing, color grading + AI color matching, auto-captioning, and smart exports.',
    category: 'Video Production',
    level: 'Beginner',
    url: '/courses/video-editing-post-production',
    advantages: [
      'Master Adobe Premiere Pro, DaVinci Resolve, and Final Cut Pro',
      'Learn professional color grading and cinematic looks',
      'Speed up workflow with AI auto-captioning and smart tools',
      'Create engaging edits with music, transitions, and effects',
      'Export optimized videos for all platforms (YouTube, Instagram, TikTok)'
    ],
    jobOpportunities: [
      'Video Editor for Production Companies',
      'YouTube & Social Media Video Editor',
      'Corporate Video Editor',
      'Freelance Video Editor',
      'Content Creator for Brands & Agencies'
    ],
    whatYouCanDo: [
      'Edit videos for YouTube creators, businesses, and events',
      'Create viral-worthy content for social media',
      'Offer editing services to videographers and content creators',
      'Produce promotional videos, ads, and commercials',
      'Build a career as a full-time video editor'
    ]
  },
  {
    title: 'Professional Graphic Design',
    code: 'GRAPH-GD-001',
    duration: '3 months',
    fee: '600,000 UGX',
    description: 'Design for print and digital media + AI-powered mockups, typography suggestions, and rapid ideation.',
    category: 'Graphic Design',
    level: 'Beginner',
    url: '/courses/professional-graphic-design',
    advantages: [
      'Master Adobe Illustrator, Photoshop, and InDesign',
      'Learn design principles, color theory, and typography',
      'Create logos, posters, flyers, and branding materials',
      'Use AI tools for rapid mockups and design variations',
      'Build a professional design portfolio'
    ],
    jobOpportunities: [
      'Graphic Designer for Agencies & Brands',
      'Freelance Designer',
      'Social Media Graphics Designer',
      'Print & Publication Designer',
      'Branding & Identity Designer'
    ],
    whatYouCanDo: [
      'Design logos, business cards, and brand identities',
      'Create marketing materials for print and digital',
      'Offer freelance design services to businesses',
      'Design social media graphics and ad campaigns',
      'Work with advertising agencies and marketing teams'
    ]
  },
  {
    title: '2D Animation & Motion Graphics',
    code: 'ANIM-2D-001',
    duration: '3 months',
    fee: '800,000 UGX',
    description: 'Create animated content and motion graphics + AI character rigging and automated in-betweening.',
    category: 'Animation',
    level: 'Beginner',
    url: '/courses/2d-animation-motion-graphics',
    advantages: [
      'Master Adobe After Effects and animation principles',
      'Create eye-catching motion graphics and animated logos',
      'Learn character animation and storytelling techniques',
      'Use AI tools for faster rigging and in-betweening',
      'Develop skills in kinetic typography and visual effects'
    ],
    jobOpportunities: [
      'Motion Graphics Designer for TV & Media',
      'Animator for Marketing Agencies',
      'Social Media Animator',
      'Explainer Video Creator',
      'Freelance Motion Designer'
    ],
    whatYouCanDo: [
      'Create animated explainer videos for businesses',
      'Design motion graphics for ads, TV, and social media',
      'Animate logos, titles, and visual effects',
      'Produce animated content for YouTube and digital platforms',
      'Offer animation services to brands and content creators'
    ]
  },
  {
    title: 'Digital Content & Social Media',
    code: 'SOCIAL-DCM-001',
    duration: '2 months',
    fee: '400,000 UGX',
    description: 'Content creation and social media marketing + ChatGPT for captions and AI content scheduling.',
    category: 'Social Media',
    level: 'Beginner',
    url: '/courses/digital-content-social-media',
    advantages: [
      'Learn platform-specific strategies for Instagram, TikTok, YouTube, and Facebook',
      'Master content creation, photography, and video for social media',
      'Use AI tools like ChatGPT for captions, hashtags, and content ideas',
      'Understand analytics, engagement, and algorithm optimization',
      'Build a personal brand or grow business accounts'
    ],
    jobOpportunities: [
      'Social Media Manager',
      'Content Creator & Influencer',
      'Digital Marketing Specialist',
      'Community Manager for Brands',
      'Freelance Social Media Consultant'
    ],
    whatYouCanDo: [
      'Manage social media accounts for businesses and brands',
      'Create viral content and grow engaged audiences',
      'Develop content strategies and campaigns',
      'Monetize your social media presence as an influencer',
      'Offer social media management services to clients'
    ]
  },
  {
    title: 'Full Multimedia Masterclass',
    code: 'MASTER-MULTI-001',
    duration: '6 months',
    fee: '4,500,000 UGX',
    description: 'Complete creative program: photography, video, design, animation + full AI workflow integration.',
    category: 'Multimedia',
    level: 'Beginner',
    url: '/courses/full-multimedia-masterclass',
    advantages: [
      'All-in-one training covering photography, video, design, and animation',
      'Save money with bundled comprehensive training',
      'Become a versatile creative professional with multiple skills',
      'Integrated AI workflow across all disciplines',
      'Industry-recognized certification upon completion'
    ],
    jobOpportunities: [
      'Multimedia Designer for Agencies',
      'Creative Director',
      'Full-Service Creative Freelancer',
      'Content Producer for Media Houses',
      'Marketing & Branding Specialist'
    ],
    whatYouCanDo: [
      'Offer complete creative services to clients',
      'Launch your own multimedia production company',
      'Work on diverse projects across all creative fields',
      'Handle end-to-end production from concept to delivery',
      'Command higher rates with comprehensive skill set'
    ]
  },
  // ── Intermediate ──────────────────────────────────────────────────────
  {
    title: 'Drone Photography & Aerial Videography',
    code: 'PHOTO-DRONE-001',
    duration: '1.5 months',
    fee: '600,000 UGX',
    description: 'Fly professional drones for aerial shoots + AI-driven tracking, cinematic post-processing, and automated flight paths.',
    category: 'Photography',
    level: 'Intermediate',
    url: '/courses/drone-photography-aerial-videography',
    advantages: [
      'Hands-on training with professional DJI drones',
      'Learn aerial cinematography and composition techniques',
      'Master automated flight paths and intelligent tracking',
      'Understand drone regulations and safety protocols in Uganda',
      'Create stunning aerial content for real estate, events, and film'
    ],
    jobOpportunities: [
      'Aerial Photographer & Videographer',
      'Real Estate Drone Specialist',
      'Event & Wedding Drone Operator',
      'Commercial Drone Pilot',
      'Film & TV Aerial Cinematographer'
    ],
    whatYouCanDo: [
      'Capture breathtaking aerial photos and videos',
      'Offer drone services to real estate agencies',
      'Film cinematic aerial footage for commercials and films',
      'Document events, weddings, and projects from the sky',
      'Create unique content that stands out in the market'
    ]
  },
  {
    title: 'UI/UX Design & Prototyping',
    code: 'UIUX-001',
    duration: '3 months',
    fee: '650,000 UGX',
    description: 'Design apps and websites: wireframing, user research, Figma + AI-assisted prototyping and user flow optimization.',
    category: 'UI/UX Design',
    level: 'Intermediate',
    url: '/courses/ui-ux-design-prototyping',
    advantages: [
      'Master Figma, Adobe XD, and modern design tools',
      'Learn user research, wireframing, and prototyping',
      'Understand user psychology and design thinking',
      'Use AI tools for rapid prototyping and user testing',
      'Build interactive prototypes for apps and websites'
    ],
    jobOpportunities: [
      'UI/UX Designer for Tech Companies',
      'Product Designer',
      'Mobile App Designer',
      'Web Designer',
      'Freelance UI/UX Consultant'
    ],
    whatYouCanDo: [
      'Design beautiful and functional user interfaces',
      'Create wireframes and prototypes for apps and websites',
      'Improve user experience and conversion rates',
      'Work with startups and tech companies on digital products',
      'Command premium rates in the high-demand UX field'
    ]
  },
  // ── Advanced ──────────────────────────────────────────────────────────
  {
    title: 'Commercial & Studio Photography',
    code: 'PHOTO-ADV-001',
    duration: '3 months',
    fee: '750,000 UGX',
    description: 'Advanced studio lighting, commercial shoots, product photography + AI enhancement for client delivery.',
    category: 'Photography',
    level: 'Advanced',
    url: '/courses/commercial-studio-photography',
    advantages: [
      'Master advanced studio lighting setups and modifiers',
      'Learn high-end product photography techniques',
      'Work with models, stylists, and creative teams',
      'Understand client management and commercial workflows',
      'Use AI for fast client-ready image delivery'
    ],
    jobOpportunities: [
      'Commercial Photographer',
      'Studio Photography Business Owner',
      'Fashion & Beauty Photographer',
      'Product Photography Specialist',
      'Advertising Photographer'
    ],
    whatYouCanDo: [
      'Shoot for brands, magazines, and advertising campaigns',
      'Open and run your own photography studio',
      'Specialize in high-paying product and fashion photography',
      'Work with modeling agencies and fashion brands',
      'Build a lucrative commercial photography business'
    ]
  },
  {
    title: 'Cinematography & Film Production',
    code: 'VIDEO-CINE-001',
    duration: '3 months',
    fee: '800,000 UGX',
    description: 'Advanced cinematography, film lighting, directing + AI color grading and automated dailies.',
    category: 'Video Production',
    level: 'Advanced',
    url: '/courses/cinematography-film-production',
    advantages: [
      'Master cinematic camera techniques and movements',
      'Learn professional film lighting and mood creation',
      'Understand directing, shot composition, and storytelling',
      'Work with cinema cameras and professional gear',
      'Use AI for efficient color grading and workflow'
    ],
    jobOpportunities: [
      'Cinematographer for Film & TV',
      'Director of Photography (DP)',
      'Commercial Director',
      'Music Video Director',
      'Documentary Filmmaker'
    ],
    whatYouCanDo: [
      'Shoot cinematic films, commercials, and music videos',
      'Work as a DP on major productions',
      'Direct and produce your own films',
      'Collaborate with production companies and filmmakers',
      'Create visually stunning content for high-end clients'
    ]
  },
  {
    title: 'VFX & Advanced Post-Production',
    code: 'VIDEO-VFX-001',
    duration: '3 months',
    fee: '750,000 UGX',
    description: 'Green screen compositing, visual effects, motion tracking + AI rotoscoping and intelligent keying.',
    category: 'Video Production',
    level: 'Advanced',
    url: '/courses/vfx-advanced-post-production',
    advantages: [
      'Master green screen and compositing techniques',
      'Learn 3D tracking, rotoscoping, and VFX integration',
      'Work with industry-standard tools like After Effects and Nuke',
      'Use AI for faster rotoscoping and keying',
      'Create Hollywood-style visual effects'
    ],
    jobOpportunities: [
      'VFX Artist for Film & TV',
      'Compositor',
      'Motion Graphics & VFX Designer',
      'Post-Production Specialist',
      'Freelance VFX Professional'
    ],
    whatYouCanDo: [
      'Create stunning visual effects for films and commercials',
      'Composite green screen footage seamlessly',
      'Add CGI elements and effects to live-action footage',
      'Work on blockbuster films and high-budget productions',
      'Offer premium VFX services to production companies'
    ]
  },
  {
    title: 'Brand Identity & Corporate Design',
    code: 'GRAPH-BRAND-001',
    duration: '3 months',
    fee: '700,000 UGX',
    description: 'Build brand identities: logo systems, brand guidelines + AI logo variations and brand asset generation.',
    category: 'Graphic Design',
    level: 'Advanced',
    url: '/courses/brand-identity-corporate-design',
    advantages: [
      'Learn strategic brand development and identity design',
      'Create comprehensive brand guidelines and style guides',
      'Master logo design, typography, and visual systems',
      'Use AI for rapid brand exploration and variations',
      'Understand brand psychology and market positioning'
    ],
    jobOpportunities: [
      'Brand Identity Designer',
      'Creative Director',
      'Corporate Design Specialist',
      'Branding Consultant',
      'Visual Identity Designer'
    ],
    whatYouCanDo: [
      'Design complete brand identities for businesses',
      'Create professional brand guidelines and systems',
      'Rebrand companies and launch new brands',
      'Offer high-value branding services to corporations',
      'Build a reputation as a brand strategist'
    ]
  },
  {
    title: '3D Animation & Visual Effects',
    code: '3D ANIM-407',
    duration: '4 months',
    fee: '1,200,000 UGX',
    description: '3D modeling, rigging, texturing in Blender + AI texture generation and render optimization.',
    category: 'Animation',
    level: 'Advanced',
    url: '/courses/3d-animation-visual-effects',
    advantages: [
      'Master Blender for 3D modeling, animation, and rendering',
      'Learn character rigging, texturing, and lighting',
      'Create realistic 3D animations and visual effects',
      'Use AI for texture generation and render optimization',
      'Develop skills for gaming, film, and advertising'
    ],
    jobOpportunities: [
      '3D Animator',
      '3D Modeler for Games & Film',
      'VFX Artist',
      'Motion Graphics Designer',
      'Architectural Visualization Specialist'
    ],
    whatYouCanDo: [
      'Create 3D characters, objects, and environments',
      'Animate 3D content for films, games, and ads',
      'Produce architectural visualizations and walkthroughs',
      'Design 3D graphics for product demos and explainers',
      'Work in the booming gaming and VFX industries'
    ]
  },
  {
    title: 'Digital Marketing & Paid Advertising',
    code: 'SOCIAL-DM-001',
    duration: '2.5 months',
    fee: '550,000 UGX',
    description: 'Facebook & Google Ads, campaign strategy + AI audience targeting and performance prediction.',
    category: 'Social Media',
    level: 'Advanced',
    url: '/courses/digital-marketing-paid-advertising',
    advantages: [
      'Master Facebook Ads, Google Ads, and Instagram advertising',
      'Learn audience targeting, campaign optimization, and analytics',
      'Understand conversion tracking and ROI measurement',
      'Use AI for audience insights and performance prediction',
      'Create profitable ad campaigns for any business'
    ],
    jobOpportunities: [
      'Digital Marketing Specialist',
      'Paid Ads Manager',
      'Performance Marketing Consultant',
      'E-commerce Marketing Expert',
      'Freelance Digital Marketer'
    ],
    whatYouCanDo: [
      'Run profitable ad campaigns for businesses',
      'Help brands grow through targeted advertising',
      'Optimize campaigns for maximum ROI',
      'Offer digital marketing services and consultancy',
      'Build a lucrative career in high-demand digital marketing'
    ]
  },
  // ── AI & Technology ───────────────────────────────────────
  {
    title: 'AI Tools for Creatives',
    code: 'AI-TOOLS-001',
    duration: '2 months',
    fee: '500,000 UGX',
    description: 'Harness the power of AI for content creation, image generation, video enhancement, and creative workflow automation using Midjourney, Adobe Firefly, and ChatGPT.',
    category: 'AI & Technology',
    level: 'Beginner',
    isNew: true,
    url: '/courses/ai-tools-for-creatives',
    advantages: [
      'Master cutting-edge AI tools like Midjourney, ChatGPT, and Adobe Firefly',
      'Generate stunning images and art with AI',
      'Automate repetitive creative tasks and boost productivity',
      'Stay ahead of industry trends with emerging AI technology',
      'Integrate AI into your creative workflow seamlessly'
    ],
    jobOpportunities: [
      'AI Content Creator',
      'Creative Automation Specialist',
      'AI-Powered Designer',
      'Digital Content Producer',
      'AI Workflow Consultant'
    ],
    whatYouCanDo: [
      'Generate unique AI art and graphics for clients',
      'Create content faster with AI-assisted tools',
      'Offer AI-powered creative services',
      'Enhance photos and videos using AI',
      'Position yourself as a cutting-edge creative professional'
    ]
  },
]

export default courses
